import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../../api/api'
import { useActions } from '../../hooks/useAction'
import { useTypedSelector } from '../../hooks/useTypedSelector'
import Currency from '../../models/currency'
import DataModel from '../../models/dataModel'
import Extensions from '../../models/extensions'
import Notification from '../../models/notification'
import { CheckItemsFilters } from '../../models/pageModels/table/tableExtensions'
import { getStaleHours, isServiceStale, plural } from '../../models/maintenance'
import { getHiddenServices, saveHiddenServices } from '../../models/hiddenServices'
import { ILanguage } from '../../models/language/language'
import { ServicesFee } from '../../types/table/requestData'
import { SingleItem } from '../../types/table/singleItem'
import { EPriceType, EServices, TableHelper } from '../../types/tableHelper'
import styles from './css/PriceCheck.module.css'

const QUICK_CURRENCIES = ['USD', 'EUR', 'CNY', 'RUB', 'UAH', 'KZT', 'TRY', 'PLN', 'JPY', 'GBP', 'AUD']

const CURRENCY_SYMBOLS: Record<string, string> = {
	USD: '$', EUR: '€', CNY: '¥', RUB: '₽', UAH: '₴', KZT: '₸',
	TRY: '₺', PLN: 'zł', JPY: '¥', GBP: '£', AUD: 'A$', THB: '฿',
	BYN: 'Br', NOK: 'kr', SEK: 'kr', HKD: 'HK$', SGD: 'S$', CHF: 'Fr',
	CZK: 'Kč', HUF: 'Ft', INR: '₹', IDR: 'Rp', NZD: 'NZ$', PHP: '₱',
	BRL: 'R$', ILS: '₪', KRW: '₩', MXN: '$', QAR: 'QR', SAR: 'SR',
	TWD: 'NT$', VND: '₫', ZAR: 'R', AED: 'د.إ', KWD: 'KD', MYR: 'RM',
	COP: '$', CRC: '₡', PEN: 'S/', UYU: '$U', ARS: '$',
}

const getCurrencyLabel = (code: string) => {
	const sym = CURRENCY_SYMBOLS[code]
	return sym ? `${sym} ${code}` : code
}

const getSortOptions = (lang: ILanguage) => [
	{ label: lang.PriceCheck_Sort_PriceAsc,  sortBy: 'price' as const, desc: false },
	{ label: lang.PriceCheck_Sort_PriceDesc, sortBy: 'price' as const, desc: true  },
	{ label: lang.PriceCheck_Sort_NameAsc,   sortBy: 'name'  as const, desc: false },
	{ label: lang.PriceCheck_Sort_NameDesc,  sortBy: 'name'  as const, desc: true  },
]

// Валюта, выбранная в окне, живёт между открытиями: раньше окно каждый раз
// сбрасывалось на валюту таблицы и выбор пользователя пропадал.
const CURRENCY_STORAGE_KEY = 'pricecheck_currency'

const readSavedCurrency = (): string | null => {
	try {
		return localStorage.getItem(CURRENCY_STORAGE_KEY)
	} catch {
		return null
	}
}

const saveCurrency = (code: string) => {
	try {
		localStorage.setItem(CURRENCY_STORAGE_KEY, code)
	} catch {
		/* приватный режим или заблокированное хранилище — не критично */
	}
}

export interface PriceCheckProps { appId: number }

interface ServiceRow {
	key: EServices
	price: number
	sortPrice: number   // конвертированная цена в текущей валюте — используется для сортировки и рангов
	count: number
	maxCount: number
	name: string
	rank: number
	isBest: boolean
	updatedAt: number
}

type FreshnessLevel = 'green' | 'yellow' | 'orange' | 'red'

interface FreshnessInfo {
	level: FreshnessLevel
	text: string
	tooltip: string
}

const UNLIMITED = -1                 // безлимитное предложение
const SLOTS = 10                     // карточек в колонке
const MAX_VISIBLE = SLOTS * 2 - 1    // -1 слот под кнопку «Показать ещё»

const ageMinutes = (unixSeconds: number) => (Date.now() / 1000 - unixSeconds) / 60

const toService = (name: string): EServices | undefined => {
	// только имена-ключи enum дают число; числовые строки ("0","1") из reverse-mapping отбрасываем
	const value = EServices[name as keyof typeof EServices]
	return typeof value === 'number' ? (value as unknown as EServices) : undefined
}

const isPriceTypeInverted = (pt: EPriceType) => pt === EPriceType.Deposit || pt === EPriceType.Order

const formatCount = (n: number): string => (n ?? 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')

const withPlural = (n: number, forms: string): string => `${formatCount(n)} ${plural(n, forms)}`

const formatOffers = (count: number, maxCount: number, lang: ILanguage): string =>
	count === UNLIMITED
		? lang.PriceCheck_Unlimited
		: maxCount > 0
			? `${formatCount(count)}/${formatCount(maxCount)} ${plural(maxCount, lang.PriceCheck_Offers)}`
			: withPlural(count, lang.PriceCheck_Offers)

const getUpdatedText = (unixSeconds: number, lang: ILanguage): string => {
	if (!unixSeconds) return lang.PriceCheck_Updated_Unknown
	const ageMin = ageMinutes(unixSeconds)
	if (ageMin < 1)  return lang.PriceCheck_Updated_Now
	if (ageMin < 60) return `${Math.round(ageMin)} ${lang.PriceCheck_Updated_Minutes}`
	return `${Math.floor(ageMin / 60)} ${lang.PriceCheck_Updated_Hours}`
}

/**
 * Цвет отметки свежести. Красный зажигается ровно по тому же порогу, по которому
 * появляется метка техработ (у каждого сервиса свой, см. maintenance.ts), иначе
 * строка горела красным без метки, а метка появлялась без красного.
 */
const getFreshnessInfo = (unixSeconds: number, service: EServices, lang: ILanguage): FreshnessInfo => {
	if (!unixSeconds) return { level: 'green', text: '', tooltip: lang.PriceCheck_UpdateTimeUnknown }
	const ageMin = ageMinutes(unixSeconds)
	const dt = new Date(unixSeconds * 1000)
	const tooltip = `${lang.PriceCheck_PriceUpdatedAt} ${dt.toLocaleString(lang.PriceCheck_DateLocale, {
		day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
	})}`
	const staleMin = getStaleHours(service) * 60
	const m = lang.PriceCheck_Short_Minutes
	const h = lang.PriceCheck_Short_Hours
	const d = lang.PriceCheck_Short_Days
	if (ageMin < 10)       return { level: 'green',  text: '',                          tooltip }
	if (ageMin < 30)       return { level: 'yellow', text: `${Math.round(ageMin)}${m}`, tooltip }
	if (ageMin < staleMin) return { level: 'orange', text: ageMin < 120 ? `${Math.round(ageMin)}${m}` : `${Math.floor(ageMin / 60)}${h}`, tooltip }
	return                        { level: 'red',    text: ageMin < 2880 ? `${Math.floor(ageMin / 60)}${h}` : `${Math.floor(ageMin / 1440)}${d}`, tooltip }
}

const SkeletonRow: FC = () => (
	<div className={styles.cmSkeletonRow}>
		<div className={`${styles.cmSkel} ${styles.cmSkelRank}`} />
		<div className={`${styles.cmSkel} ${styles.cmSkelIcon}`} />
		<div className={styles.cmSkelInfo}>
			<div className={`${styles.cmSkel} ${styles.cmSkelName}`} />
			<div className={`${styles.cmSkel} ${styles.cmSkelCount}`} />
		</div>
		<div className={`${styles.cmSkel} ${styles.cmSkelPrice}`} />
		<div className={`${styles.cmSkel} ${styles.cmSkelDot}`} />
	</div>
)

const SkeletonRows: FC = () => (
	<div className={styles.cmGridLoading}>
		<div className={styles.cmGridCol}>
			{Array.from({ length: SLOTS }, (_, i) => <SkeletonRow key={i} />)}
		</div>
		<div className={styles.cmGridCol}>
			{Array.from({ length: SLOTS }, (_, i) => <SkeletonRow key={i} />)}
		</div>
	</div>
)

interface RowCardProps {
	row: ServiceRow
	appId: number
	cf: CheckItemsFilters
	lang: ILanguage
	onHide?: (service: EServices) => void
}

const RowCard: FC<RowCardProps> = ({ row, appId, cf, lang, onHide }) => {
	const fresh = getFreshnessInfo(row.updatedAt, row.key, lang)
	const medalClass = row.rank <= 3 ? styles[`cmMedal${row.rank}`] : ''
	// Сервис лежит: цену прячем под «техработы», но по наведению показываем
	// последнюю известную — пользователь сам решит, доверять ей или нет.
	const maintenance = isServiceStale(row.key, row.updatedAt)
	return (
		<div className={styles.cmRowWrap}>
		{onHide && (
			// Крестик вне ссылки: <button> внутри <a> ломает разметку, и клик по нему
			// не должен открывать площадку
			<button
				className={styles.cmHideBtn}
				title={lang.PriceCheck_Hide}
				aria-label={`${lang.PriceCheck_Hide}: ${TableHelper.GetServiceName(row.key)}`}
				onClick={e => { e.preventDefault(); e.stopPropagation(); onHide(row.key) }}
			>✕</button>
		)}
		<a
			className={`${styles.cmRow} ${maintenance ? styles.cmRowMaint : ''}`}
			href={Extensions.GetItemLink(row.name, appId, row.key)}
			rel='noreferrer nofollow'
			target='_blank'
			aria-label={`${TableHelper.GetServiceName(row.key)}: ${cf.currency.GetCurrencyTextByService(row.price, row.key)}`}
		>
			<div className={`${styles.cmRank} ${medalClass}`}>
				<span className={styles.cmRankNum}>{row.rank}</span>
			</div>
			<img
				className={styles.cmRowIcon}
				src={`Resources/ServicesIcons/${EServices[row.key].toLowerCase()}.png`}
				alt={TableHelper.GetServiceName(row.key)}
			/>
			<div className={styles.cmRowInfo}>
				<div className={styles.cmRowName}>
					{TableHelper.GetServiceName(row.key)}
					{row.isBest && <span className={styles.cmBestPrice}>BEST PRICE</span>}
				</div>
				<div className={styles.cmRowCount}>
					{formatOffers(row.count, row.maxCount, lang)}
				</div>
			</div>
			<div
				className={styles.cmRowPrice}
				title={maintenance ? lang.TablePage_Maintenance_HoverHint : undefined}
			>
				{maintenance && (
					<span className={styles.cmMaintTag}>{lang.TablePage_Maintenance_Tag}</span>
				)}
				<span className={maintenance ? styles.cmMaintPrice : undefined}>
					{cf.currency.GetCurrencyTextByService(row.price, row.key)}
				</span>
			</div>
			<div className={styles.cmFreshIndicator} title={fresh.tooltip}>
				{fresh.level === 'green'
					? <div className={`${styles.cmFreshDot} ${styles.cmFreshDotGreen}`} />
					: <span className={`${styles.cmFreshText} ${styles[`cmFreshText_${fresh.level}`]}`}>{fresh.text}</span>
				}
			</div>
		</a>
		</div>
	)
}

interface OverlayModalProps {
	wrapClass: string
	title: string
	closeLabel: string
	onClose: () => void
	children: React.ReactNode
}

const OverlayModal: FC<OverlayModalProps> = ({ wrapClass, title, closeLabel, onClose, children }) => (
	<div className={styles.cmMoreOverlay} onClick={onClose}>
		<div className={wrapClass} onClick={e => e.stopPropagation()}>
			{/* data-pc-modal — по нему ловушка фокуса понимает, что открыта вторичная
			    поверхность, и держит Tab внутри неё, а не в попапе под ней */}
			<div className={styles.cmMorePopup} data-pc-modal>
				<button className={styles.cmCloseBtn} onClick={onClose} aria-label={closeLabel}>✕</button>
				<div className={styles.cmMoreTitle}>{title}</div>
				{children}
			</div>
		</div>
	</div>
)

const PriceCheck: FC<PriceCheckProps> = (props: PriceCheckProps) => {
	const { lang: { Value: lang } } = useTypedSelector(x => x.lang)
	const { active, itemId, itemName: reduxItemName } = useTypedSelector(x => x.checkItem)
	const { data: fees } = useTypedSelector(x => x.fees)
	const { user } = useTypedSelector(x => x.user)

	const [checkLoading, setCheckLoading]     = useState(false)
	const [checkFilters, setCheckFilters]     = useState<CheckItemsFilters>()
	const [sortDesc, setSortDesc]             = useState(false)
	const [sortBy, setSortBy]                 = useState<'price' | 'name'>('price')
	const [openDrop, setOpenDrop]             = useState<'curr' | 'sort' | null>(null)
	const [showMoreOpen, setShowMoreOpen]     = useState(false)
	const [hiddenOpen, setHiddenOpen]         = useState(false)
	// Площадки, которые пользователь убрал из окна (VVT-232)
	const [hidden, setHidden]                 = useState<Set<EServices>>(() => getHiddenServices(user?.steamID))
	const [imgFailed, setImgFailed]           = useState(false)
	const [checkItem, setCheckItem]           = useState<SingleItem | null>(null)
	const [noAccess, setNoAccess]             = useState(false)
	const [, tick]                            = useState(0)

	const { closeCheckItem } = useActions()
	const popupRef     = useRef<HTMLDivElement>(null)
	const overlayRef   = useRef<HTMLDivElement>(null)
	const closeBtnRef  = useRef<HTMLButtonElement>(null)
	// сколько площадок ЭТОГО предмета отфильтровано списком скрытых (заполняется в buildServiceRows)
	const hiddenHereRef = useRef(0)

	useEffect(() => { setHidden(getHiddenServices(user?.steamID)) }, [user?.steamID])

	const updateHidden = useCallback((next: Set<EServices>) => {
		setHidden(next)
		saveHiddenServices(next, user?.steamID)
	}, [user?.steamID])

	const hideService = useCallback((service: EServices) => {
		updateHidden(new Set([...getHiddenServices(user?.steamID), service]))
	}, [updateHidden, user?.steamID])

	const restoreService = useCallback((service: EServices) => {
		const next = new Set(getHiddenServices(user?.steamID))
		next.delete(service)
		updateHidden(next)
	}, [updateHidden, user?.steamID])

	const restoreAll = useCallback(() => {
		updateHidden(new Set())
		setHiddenOpen(false)
	}, [updateHidden])

	const checkDisable = useCallback(() => {
		closeCheckItem(); setOpenDrop(null); setShowMoreOpen(false); setHiddenOpen(false)
	}, [closeCheckItem])

	const closeDrop = useCallback(() => setOpenDrop(null), [])

	const errorMsgRef = useRef(lang.Notification_Error)
	useEffect(() => { errorMsgRef.current = lang.Notification_Error }, [lang.Notification_Error])

	const getCheckItem = useCallback(async (isCancelled: () => boolean = () => false) => {
		// id предмета — индекс в AllNamesList на бэкенде, 0 валиден (GetSingle отклоняет только id < 0);
		// при '!itemId' предмет с id=0 не фетчился, а эффект уже ставил body.overflow=hidden —
		// страница блокировалась невидимым оверлеем (checkFilters не создан → render null)
		if (itemId < 0) return
		setCheckLoading(true)
		setNoAccess(false)
		const next = new CheckItemsFilters()
		next.currency = new Currency()
		// Валюта окна: сначала сохранённая между открытиями, иначе валюта таблицы.
		// SetCurrency возвращает null на неизвестном коде — тогда не молчим в USD,
		// а честно откатываемся на валюту таблицы (в хранилище мог остаться мусор)
		const saved = readSavedCurrency()
		if (!saved || !next.currency.SetCurrency(saved)) next.currency.SetCurrency(DataModel.Currency.Current)
		setCheckFilters(next)
		try {
			const response = await api.get<SingleItem>(`table/check/${props.appId}/${itemId}`)
			if (isCancelled()) return
			setCheckItem(response.data)
		} catch (err) {
			if (isCancelled()) return
			// Окно под Pro-подпиской: 401/403 — это не сбой, а «нет доступа»,
			// показываем понятное объяснение вместо общей ошибки и пустой таблицы
			const status = (err as { response?: { status?: number } })?.response?.status
			if (status === 401 || status === 403) setNoAccess(true)
			else Notification.ShowNotification(errorMsgRef.current)
		} finally {
			if (!isCancelled()) setCheckLoading(false)
		}
	}, [props.appId, itemId])

	// Закрытое окно остаётся в DOM ради плавного исчезновения, но его 20 кнопок и ссылок
	// оставались в обходе Tab: фокус уходил внутрь невидимого окна. inert убирает их
	// из обхода и от скринридеров, не мешая анимации.
	useEffect(() => {
		const el = overlayRef.current as (HTMLDivElement & { inert?: boolean }) | null
		if (el) el.inert = !active
	}, [active])

	useEffect(() => {
		let cancelled = false
		let focusTimer: ReturnType<typeof setTimeout>
		if (active && itemId >= 0) {
			setOpenDrop(null)
			setShowMoreOpen(false)
			setHiddenOpen(false)
			setCheckItem(null)
			setImgFailed(false)
			getCheckItem(() => cancelled)
			focusTimer = setTimeout(() => closeBtnRef.current?.focus(), 80)
			document.body.style.overflow = 'hidden'
		} else {
			setCheckItem(null)
			document.body.style.overflow = ''
		}
		return () => {
			cancelled = true
			clearTimeout(focusTimer)
			document.body.style.overflow = ''
		}
	}, [active, itemId, props.appId, getCheckItem])

	const handleFocusTrap = useCallback((e: KeyboardEvent) => {
		// Если открыта модалка («Показать ещё» или «Валюта»), она рендерится вне
		// попапа — держим фокус в ней, иначе Tab уходил под неё, в попап
		const container = overlayRef.current?.querySelector<HTMLElement>('[data-pc-modal]') ?? popupRef.current
		if (!container) return
		const focusable = Array.from(
			container.querySelectorAll<HTMLElement>(
				'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
			)
		)
		if (!focusable.length) return
		const first = focusable[0], last = focusable[focusable.length - 1]
		if (e.shiftKey) { if (document.activeElement === first) { e.preventDefault(); last.focus() } }
		else            { if (document.activeElement === last)  { e.preventDefault(); first.focus() } }
	}, [])

	useEffect(() => {
		if (!active) return
		const onKey = (e: KeyboardEvent) => {
			// Esc закрывает сначала открытую вторичную поверхность (модалку "ещё" / дропдаун валют или сортировки),
			// и только если ничего не открыто — весь попап
			if (e.key === 'Escape') {
				if (showMoreOpen) setShowMoreOpen(false)
				else if (hiddenOpen) setHiddenOpen(false)
				else if (openDrop) setOpenDrop(null)
				else checkDisable()
			}
			if (e.key === 'Tab') handleFocusTrap(e)
		}
		document.addEventListener('keydown', onKey)
		return () => document.removeEventListener('keydown', onKey)
	}, [active, showMoreOpen, hiddenOpen, openDrop, checkDisable, handleFocusTrap])

	useEffect(() => {
		if (!active) return
		const id = setInterval(() => tick(n => n + 1), 30000)
		return () => clearInterval(id)
	}, [active])

	// Таблица перерисовывается по событию items_updated из хаба, а окно держало снимок
	// с момента открытия: через полчаса все сервисы красились как несвежие, а по порогам
	// техработ окно рисовало «на техработах», хотя на сайте данные уже обновились.
	// Тихо перезапрашиваем предмет, не трогая выбранные валюту, тип цены и сортировку.
	useEffect(() => {
		if (!active || itemId < 0) return
		let cancelled = false
		const refresh = async () => {
			try {
				const response = await api.get<SingleItem>(`table/check/${props.appId}/${itemId}`)
				if (!cancelled) setCheckItem(response.data)
			} catch {
				/* молча: на экране остаются прошлые цены, они честно помечены временем */
			}
		}
		window.addEventListener('items_updated', refresh)
		return () => {
			cancelled = true
			window.removeEventListener('items_updated', refresh)
		}
	}, [active, itemId, props.appId])

	const handleOverlayClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
		if ((e.target as HTMLElement).id === 'pc-overlay') checkDisable()
	}, [checkDisable])

	const priceTypeLabels = useMemo<Record<EPriceType, string>>(() => ({
		[EPriceType.Normal]:  lang.TablePage_PriceTypeNormal,
		[EPriceType.Deposit]: lang.TablePage_PriceTypeDeposit,
		[EPriceType.Order]:   lang.TablePage_PriceTypeOrder,
		[EPriceType.Average]: lang.TablePage_PriceTypeAverage,
		[EPriceType.NoHold]:  lang.TablePage_PriceTypeNoHold,
	}), [lang])
	const getPriceTypeStr = (pt: EPriceType): string => priceTypeLabels[pt] || 'Normal'

	const buildServiceRows = (single: SingleItem): ServiceRow[] => {
		if (!single?.data || !checkFilters) return []
		const cf = checkFilters
		const isInverted = isPriceTypeInverted(cf.priceType)
		// ?? а не === null: пока комиссии не загрузились, в сторе undefined,
		// и обращение к currentFee[appId] роняло окно
		const currentFee = fees ?? DataModel.GetDefaultFees()
		const rawAll = Object.entries(single.data).map(([name, value]) => {
			const key = toService(name)
			if (key === undefined) return null
			// fee нужен только для депозита у LF/CSTrade — считаем лениво, не на каждой строке
			const price = ((): number => {
				if (cf.priceType === EPriceType.Deposit) {
					if (!value.d) return 0
					if (!TableHelper.CheckServiceForDepositCalculate(key)) return value.d
					const fee = currentFee[props.appId]?.[key] as ServicesFee
					return fee ? Extensions.Floor(value.d * (1 - (fee.fee - fee.bonus) / 100)) : value.d
				}
				return ({
					[EPriceType.Normal]:  value.n,
					[EPriceType.Order]:   value.o,
					[EPriceType.Average]: value.a,
					[EPriceType.NoHold]:  value.h,
				}[cf.priceType]) || 0
			})()
			const count    = ({ [EPriceType.Order]: value.q, [EPriceType.NoHold]: value.j }[cf.priceType]) ?? value.c
			const maxCount = value.m || 0
			const sortPrice = cf.currency.GetPriceByService(price, key)
			return { key, price, sortPrice, count, maxCount, name: single.name, updatedAt: value.t || 0 }
		}).filter(x => x !== null && x.price > 0)

		// сколько площадок этого предмета убрал пользователь — нужно, чтобы отличить
		// «ты всё скрыл» от «у предмета просто нет цен выбранного типа»
		hiddenHereRef.current = rawAll.filter(x => hidden.has(x.key)).length
		const rawRows = rawAll.filter(x => !hidden.has(x.key))

		const bestPrice = isInverted
			? Math.max(...rawRows.map(r => r.sortPrice))
			: Math.min(...rawRows.map(r => r.sortPrice))

		// Ранг по выгодности (1 = лучшая цена) — стабилен, не зависит от текущей сортировки списка,
		// поэтому медали и номер всегда согласованы с меткой BEST PRICE
		const valueRank = new Map<EServices, number>()
		;[...rawRows]
			.sort((a, b) => isInverted ? b.sortPrice - a.sortPrice : a.sortPrice - b.sortPrice)
			.forEach((r, i) => valueRank.set(r.key, i + 1))

		const sorted = [...rawRows].sort((a, b) => {
			if (sortBy === 'name') {
				const cmp = TableHelper.GetServiceName(a.key).localeCompare(TableHelper.GetServiceName(b.key))
				return sortDesc ? -cmp : cmp
			}
			if (sortBy === 'price' && !sortDesc)
				return isInverted ? b.sortPrice - a.sortPrice : a.sortPrice - b.sortPrice
			return isInverted ? a.sortPrice - b.sortPrice : b.sortPrice - a.sortPrice
		})
		return sorted.map(r => ({ ...r, rank: valueRank.get(r.key) ?? 0, isBest: r.sortPrice === bestPrice }))
	}

	const rows = useMemo<ServiceRow[]>(
		() => (active && checkItem && !checkLoading) ? buildServiceRows(checkItem) : [],
		[active, checkItem, checkLoading, checkFilters?.priceType, checkFilters?.currency?.Current, sortBy, sortDesc, fees, props.appId, hidden]
	)

	// Скрыли последние площадки из «показать ещё» — модалке нечего показывать;
	// вернули последнюю из списка скрытых — список опустел. Закрываем сами.
	useEffect(() => {
		if (showMoreOpen && rows.length <= SLOTS * 2) setShowMoreOpen(false)
	}, [showMoreOpen, rows.length])

	useEffect(() => {
		if (hiddenOpen && hidden.size === 0) setHiddenOpen(false)
	}, [hiddenOpen, hidden])

	if (!props.appId) return null
	if (!checkFilters) return null

	const cf = checkFilters
	const currencySourceArray = Array.from(cf.currency.Codes())

	const updateFilters = (mutate: (cf: CheckItemsFilters) => void) => {
		setCheckFilters(prev => {
			const next = Object.assign(new CheckItemsFilters(), prev)
			mutate(next)
			return next
		})
	}

	const selectCurrency = (c: string) => {
		saveCurrency(c)
		updateFilters(f => {
			const currency = new Currency()
			currency.Current = f.currency.Current
			currency.SetCurrency(c)
			f.currency = currency
		})
	}

	const toggleDrop = (d: 'curr' | 'sort') => setOpenDrop(prev => prev === d ? null : d)

	const isPriceInverted = isPriceTypeInverted(cf.priceType)
	const displayDesc = sortBy === 'price' && isPriceInverted ? !sortDesc : sortDesc
	const sortOptions = getSortOptions(lang)
	const currentSortLabel = sortOptions.find(o => o.sortBy === sortBy && o.desc === displayDesc)?.label ?? sortOptions[0].label

	let imageUrl = 'Resources/no-image.png'
	if (checkItem?.imageUrl) {
		imageUrl = checkItem.imageUrl.startsWith('https')
			? checkItem.imageUrl
			: Extensions.GetItemImageLink(checkItem.imageUrl, 128)
	}

	const name = checkItem?.name || reduxItemName
	const lastCount = rows.length
	const lastUpdated = rows.length ? Math.max(...rows.map(r => r.updatedAt)) : 0

	// пусто, потому что пользователь всё скрыл, а не потому что цен нет
	const allHiddenHere = !noAccess && rows.length === 0 && hiddenHereRef.current > 0

	const hasMore     = rows.length > SLOTS * 2
	const sliceCount  = hasMore ? MAX_VISIBLE : rows.length
	const visibleRows = rows.slice(0, sliceCount)
	// Сервисов меньше одной колонки — показываем одну колонку без пустых слотов:
	// на трёх сервисах правая половина окна была пустой, а окно держало 680px высоты
	const singleColumn = !checkLoading && rows.length > 0 && rows.length <= SLOTS
	const hiddenRows  = hasMore ? rows.slice(sliceCount) : []

	const col1Rows = visibleRows.slice(0, SLOTS)
	const col2Rows = visibleRows.slice(SLOTS, SLOTS * 2)

	// В модалке «ещё» порядок такой же, как в основной сетке: сверху вниз по левой
	// колонке, потом по правой. Раньше сетка раскладывала по строкам и номера прыгали
	const moreSplit   = Math.ceil(hiddenRows.length / 2)
	const moreCol1    = hiddenRows.slice(0, moreSplit)
	const moreCol2    = hiddenRows.slice(moreSplit)

	return (
		<div
			className={`${styles.overlay} ${active ? styles.overlayVisible : ''}`}
			id='pc-overlay'
			onClick={handleOverlayClick}
			aria-hidden={!active}
			ref={overlayRef}
		>
			<div className={styles.cmCornerWrap} onClick={e => e.stopPropagation()}>
			<div
				className={`${styles.cmPopup} ${singleColumn ? '' : styles.cmPopupTall}`}
				role='dialog'
				aria-modal='true'
				aria-labelledby='pc-title'
				ref={popupRef}
			>
				<button className={styles.cmCloseBtn} onClick={checkDisable} aria-label={lang.PriceCheck_Close} ref={closeBtnRef}>✕</button>

				<div className={styles.cmHead}>
					{/* заглушка — существующий no-image.png; раньше подставлялся
					    amogusSUS.png, которого в проекте нет, и картинка была битой */}
					{imgFailed || imageUrl === 'Resources/no-image.png'
						? <img src='Resources/no-image.png' alt='item' className={styles.cmHeadImg} />
						: <img src={imageUrl} alt='item' className={styles.cmHeadImg} onError={() => setImgFailed(true)} />
					}
					<div className={styles.cmHeadContent}>
						<div className={styles.cmHeadLabel}>{lang.PriceCheck_Title}</div>
						{name
							? <div id='pc-title' className={styles.cmHeadName}
								onClick={() => window.blaz?.CopyToClipboard(name)}
								title={lang.TablePage_Title_Copy}
							>{name}</div>
							: <div className={styles.cmHeadNameSkeleton} />
						}
						<div className={styles.cmHeadMeta}>
							<span className={styles.cmMetaDot}>●</span>
							<span>
								<span className={styles.cmMetaCount}>{lastCount}</span>
								{' '}{plural(lastCount, lang.PriceCheck_Services)}
							</span>
							<span className={styles.cmMetaSep}>•</span>
							<span>↻ {lang.PriceCheck_Updated}: {getUpdatedText(lastUpdated, lang)}</span>
							{hidden.size > 0 && (
								<>
									<span className={styles.cmMetaSep}>•</span>
									<button className={styles.cmHiddenChip} onClick={() => setHiddenOpen(true)}>
										{lang.PriceCheck_HiddenCount.replace('{0}', String(hidden.size))}
									</button>
								</>
							)}
						</div>
					</div>
					<div className={styles.cmHeadActions}>
						{/* На будущее
					<button className={styles.cmInfoBtn}>
						<i className='fas fa-info-circle' />
						<span>Как формируются цены?</span>
					</button>
					*/}
					</div>
				</div>

				<div className={styles.cmFilters}>
					<div className={`${styles.cmFilterGroup} ${styles.cmCurrFilter}`}>
						<div className={styles.cmFilterLabel}>{lang.PriceCheck_Label_Currency}</div>
						<div className={styles.cmDropWrapper}>
							<button
								className={`${styles.cmDropBtn} ${openDrop === 'curr' ? styles.cmDropBtnActive : ''}`}
								onClick={() => toggleDrop('curr')}
								aria-haspopup='dialog' aria-expanded={openDrop === 'curr'}
							>
								{getCurrencyLabel(cf.currency.Current)}
								<i className={`fas fa-chevron-${openDrop === 'curr' ? 'up' : 'down'}`} />
							</button>
						</div>
					</div>

					<div className={`${styles.cmFilterGroup} ${styles.cmPriceTypeGroup}`}>
						<div className={styles.cmFilterLabel}>{lang.PriceCheck_Label_PriceType}</div>
						<div className={styles.cmPriceTypePills} role='group' aria-label={lang.PriceCheck_Label_PriceType}>
							{Extensions.GetEnumValues(EPriceType).map((pt, i) => (
								<button key={i}
									className={`${styles.cmPriceTypePill} ${cf.priceType === pt ? styles.cmPriceTypePillActive : ''}`}
									onClick={() => updateFilters(f => { f.priceType = pt })}
									aria-pressed={cf.priceType === pt}
								>{getPriceTypeStr(pt)}</button>
							))}
						</div>
					</div>

					<div className={styles.cmFiltersRight}>
						<div className={styles.cmFilterGroup}>
							<div className={styles.cmFilterLabel}>{lang.PriceCheck_Label_Sort}</div>
							<div className={styles.cmDropWrapper}>
								<button
									className={`${styles.cmDropBtn} ${styles.cmSortBtn} ${openDrop === 'sort' ? styles.cmDropBtnActive : ''}`}
									onClick={() => toggleDrop('sort')}
									aria-haspopup='listbox' aria-expanded={openDrop === 'sort'}
								>
									<i className='fas fa-sort' />
									<span>{currentSortLabel}</span>
									<i className={`fas fa-chevron-${openDrop === 'sort' ? 'up' : 'down'}`} />
								</button>
								{openDrop === 'sort' && (
									<div className={`${styles.cmDropList} ${styles.cmSortDropList}`} role='listbox'>
										{sortOptions.map((opt, i) => (
											<div key={i} role='option'
												aria-selected={opt.sortBy === sortBy && opt.desc === displayDesc}
												className={`${styles.cmDropItem} ${opt.sortBy === sortBy && opt.desc === displayDesc ? styles.cmDropItemActive : ''}`}
												onClick={() => { setSortBy(opt.sortBy); setSortDesc(isPriceInverted && opt.sortBy === 'price' ? !opt.desc : opt.desc); setOpenDrop(null) }}
											>{opt.label}</div>
										))}
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				{(checkLoading || rows.length === 0) ? (
					<div className={styles.cmStateWrap}>
						<div className={`${styles.cmStateLayer} ${(rows.length === 0 && !checkLoading) ? styles.cmStateLayerHidden : ''}`}>
							<SkeletonRows />
						</div>
						{rows.length === 0 && !checkLoading && (
							<div className={styles.cmEmpty} style={{ position: 'absolute', inset: 0 }}>
								<i className={`fas ${noAccess ? 'fa-lock' : allHiddenHere ? 'fa-eye-slash' : 'fa-search'} ${styles.cmEmptyIcon}`} />
								<span>{noAccess ? lang.PriceCheck_NoAccess_Title : allHiddenHere ? lang.PriceCheck_AllHidden : lang.PriceCheck_Empty}</span>
								{noAccess && <span className={styles.cmEmptySub}>{lang.PriceCheck_NoAccess_Sub}</span>}
								{/* скрыта одна площадка — «Вернуть все» не о чем, возвращаем именно её */}
								{allHiddenHere && (
									hidden.size > 1
										? <button className={styles.cmRestoreAllBtn} onClick={restoreAll}>{lang.PriceCheck_RestoreAll}</button>
										: <button className={styles.cmRestoreAllBtn} onClick={() => restoreService([...hidden][0])}>{lang.PriceCheck_Restore}</button>
								)}
							</div>
						)}
					</div>
				) : singleColumn ? (
					<div className={`${styles.cmGrid} ${styles.cmGridSingle}`} onClick={closeDrop}>
						<div className={styles.cmGridCol}>
							{visibleRows.map(row => (
								<RowCard key={row.key} row={row} appId={props.appId} cf={cf} lang={lang} onHide={hideService} />
							))}
						</div>
					</div>
				) : (
					<div className={styles.cmGrid} onClick={closeDrop}>
						<div className={styles.cmGridCol}>
							{Array.from({ length: SLOTS }, (_, i) => col1Rows[i]
								? <RowCard key={col1Rows[i].key} row={col1Rows[i]} appId={props.appId} cf={cf} lang={lang} onHide={hideService} />
								: <div key={`empty1-${i}`} className={styles.cmEmptyRow} />
							)}
						</div>
						<div className={styles.cmGridCol}>
							{Array.from({ length: SLOTS }, (_, i) => {
								const isLast = i === SLOTS - 1
								if (isLast && hasMore) return (
									<button key='more' className={styles.cmShowMore} onClick={() => setShowMoreOpen(true)}>
										<i className='fas fa-ellipsis-h' />
										<span>{lang.PriceCheck_ShowMore} {withPlural(hiddenRows.length, lang.PriceCheck_Services)}</span>
									</button>
								)
								return col2Rows[i]
									? <RowCard key={col2Rows[i].key} row={col2Rows[i]} appId={props.appId} cf={cf} lang={lang} onHide={hideService} />
									: <div key={`empty2-${i}`} className={styles.cmEmptyRow} />
							})}
						</div>
					</div>
				)}

				<div className={styles.cmBottom}>
					<span className={styles.cmBottomLabel}>{lang.PriceCheck_QuickCurrency}</span>
					<div className={styles.cmCurrPills}>
						{QUICK_CURRENCIES.map((c, i) => (
							<button key={i}
								className={`${styles.cmQuickCurr} ${cf.currency.Current === c ? styles.cmQuickCurrActive : ''}`}
								onClick={() => selectCurrency(c)}
							>{getCurrencyLabel(c)}</button>
						))}
					</div>
					<button
						className={`${styles.cmQuickCurr} ${styles.cmQuickCurrDots}`}
						onClick={() => setOpenDrop('curr')}
						title={lang.PriceCheck_AllCurrencies}
					>···</button>
				</div>

			</div>
		</div>

			{showMoreOpen && (
				<OverlayModal
					wrapClass={styles.cmMoreWrap}
					title={`${lang.PriceCheck_MoreTitle} — ${hiddenRows.length}`}
					closeLabel={lang.PriceCheck_Close}
					onClose={() => setShowMoreOpen(false)}
				>
					<div className={styles.cmMoreGrid}>
						<div className={styles.cmGridCol}>
							{moreCol1.map(row => (
								<RowCard key={row.key} row={row} appId={props.appId} cf={cf} lang={lang} onHide={hideService} />
							))}
						</div>
						<div className={styles.cmGridCol}>
							{moreCol2.map(row => (
								<RowCard key={row.key} row={row} appId={props.appId} cf={cf} lang={lang} onHide={hideService} />
							))}
						</div>
					</div>
				</OverlayModal>
			)}

			{hiddenOpen && (
				<OverlayModal
					wrapClass={styles.cmCurrModalWrap}
					title={`${lang.PriceCheck_HiddenTitle} — ${hidden.size}`}
					closeLabel={lang.PriceCheck_Close}
					onClose={() => setHiddenOpen(false)}
				>
					<div className={styles.cmHiddenList}>
						{[...hidden].map(service => (
							<div key={service} className={styles.cmHiddenRow}>
								<img
									className={styles.cmRowIcon}
									src={`Resources/ServicesIcons/${EServices[service].toLowerCase()}.png`}
									alt={TableHelper.GetServiceName(service)}
								/>
								<span className={styles.cmHiddenName}>{TableHelper.GetServiceName(service)}</span>
								<button className={styles.cmRestoreBtn} onClick={() => restoreService(service)}>
									{lang.PriceCheck_Restore}
								</button>
							</div>
						))}
					</div>
					{/* при одной скрытой площадке «Вернуть все» дублирует кнопку в строке */}
					{hidden.size > 1 && (
						<div className={styles.cmHiddenFooter}>
							<button className={styles.cmRestoreAllBtn} onClick={restoreAll}>{lang.PriceCheck_RestoreAll}</button>
						</div>
					)}
				</OverlayModal>
			)}

			{openDrop === 'curr' && (
				<OverlayModal
					wrapClass={styles.cmCurrModalWrap}
					title={lang.PriceCheck_CurrencyCount.replace('{0}', String(currencySourceArray.length))}
					closeLabel={lang.PriceCheck_Close}
					onClose={() => setOpenDrop(null)}
				>
					<div className={styles.cmCurrGrid}>
						{currencySourceArray.map((c, i) => (
							<button key={i}
								className={`${styles.cmCurrGridItem} ${cf.currency.Current === c ? styles.cmCurrGridItemActive : ''}`}
								onClick={() => { selectCurrency(c); setOpenDrop(null) }}
							>{getCurrencyLabel(c)}</button>
						))}
					</div>
				</OverlayModal>
			)}
		</div>
	)
}

export default PriceCheck
